import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'dark' | 'light';
/** What the trader picked. `system` follows the device instead of pinning. */
export type ThemeChoice = Theme | 'system';

const STORAGE_KEY = 'pipyar-theme';

/*
 * A module-level store rather than per-hook state.
 *
 * Eight components call useTheme, and every chart takes the resolved theme as
 * a prop. With independent useState each copy only learned of a change when it
 * happened to remount, so switching in the header could leave a chart painted
 * from the old palette. One store with one subscription keeps every caller on
 * the same answer.
 *
 * It is also what makes `system` work at all: the device can change the answer
 * with no React event behind it, so something has to listen and tell everyone.
 */
const listeners = new Set<() => void>();

const darkQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function readStoredChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  } catch {
    /* private mode — fall through to the device */
  }
  // Nothing recorded: follow the device, which is what the pre-paint script in
  // index.html already assumed.
  return 'system';
}

let choice: ThemeChoice = readStoredChoice();

const deviceTheme = (): Theme => (darkQuery?.matches ? 'dark' : 'light');
const resolve = (c: ThemeChoice): Theme => (c === 'system' ? deviceTheme() : c);

function applyToDocument(): void {
  document.documentElement.classList.toggle('dark', resolve(choice) === 'dark');
}

function emit(): void {
  for (const listener of listeners) listener();
}

darkQuery?.addEventListener('change', () => {
  // Only the device-following choice is affected, but re-applying costs
  // nothing and keeps the class honest if another tab changed the choice.
  if (choice !== 'system') return;
  applyToDocument();
  emit();
});

/*
 * Snapshot identity has to be stable, or useSyncExternalStore re-renders
 * forever: it compares the returned object by reference.
 */
let snapshot: { theme: Theme; choice: ThemeChoice } = { theme: resolve(choice), choice };

function getSnapshot(): { theme: Theme; choice: ThemeChoice } {
  const theme = resolve(choice);
  if (snapshot.theme !== theme || snapshot.choice !== choice) snapshot = { theme, choice };
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setThemeChoice(next: ThemeChoice): void {
  choice = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* private mode — the choice just will not persist. */
  }
  applyToDocument();
  emit();
}

/**
 * Cycle order for the header button: an explicit pair first, then the device.
 *
 * Device-follow sits last on purpose. Someone reaching for the button usually
 * wants the other palette right now; following the device is the considered
 * setting you arrive at, not the one you hit by accident.
 */
const CYCLE: readonly ThemeChoice[] = ['dark', 'light', 'system'];

export function useTheme() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const toggle = useCallback(() => {
    const at = CYCLE.indexOf(state.choice);
    setThemeChoice(CYCLE[(at + 1) % CYCLE.length]!);
  }, [state.choice]);

  return { theme: state.theme, choice: state.choice, setChoice: setThemeChoice, toggle };
}
